import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, CheckCircle, XCircle } from 'lucide-react';

/**
 * Generic workflow footer with navigation buttons
 *
 * @param {Object} props
 * @param {number} props.currentStep - Current step number
 * @param {number} props.totalSteps - Total number of steps
 * @param {Function} props.onPrevious - Previous button handler
 * @param {Function} props.onNext - Next button handler
 * @param {Function} props.onSubmit - Submit button handler
 * @param {boolean} props.canSubmit - Whether submit is enabled
 * @param {string} [props.submitLabel] - Submit button label (défaut : common.actions.save)
 * @param {string} [props.accentColor='amber'] - Accent color for next button
 * @param {Function} [props.onCancel] - Optional cancel handler (shows cancel button if provided)
 * @param {string} [props.cancelLabel] - Cancel button label (défaut : common.actions.cancel)
 * @returns {JSX.Element}
 */
function WorkflowFooter({
  currentStep,
  totalSteps,
  onPrevious,
  onNext,
  onSubmit,
  canSubmit,
  submitLabel,
  accentColor = 'amber',
  onCancel,
  cancelLabel
}) {
  const { t } = useTranslation();
  const submitText = submitLabel ?? t('common.actions.save');
  const cancelText = cancelLabel ?? t('common.actions.cancel');
  const accentClasses = {
    amber: 'bg-amber-500 hover:bg-amber-600',
    blue: 'bg-blue-500 hover:bg-blue-600',
    red: 'bg-red-500 hover:bg-red-600',
    green: 'bg-green-500 hover:bg-green-600',
    purple: 'bg-purple-500 hover:bg-purple-600',
  };

  const accentClass = accentClasses[accentColor] || accentClasses.amber;

  return (
    <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
      <div className="flex items-center gap-2">
        <button
          onClick={onPrevious}
          disabled={currentStep === 1}
          className={`px-6 py-2 rounded-lg flex items-center gap-2 ${
            currentStep === 1
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
          }`}
        >
          <ChevronLeft className="w-5 h-5" />
          {t('common.actions.previous')}
        </button>

        {onCancel && (
          <button
            onClick={onCancel}
            className="px-4 py-2 text-red-600 hover:text-red-800 flex items-center gap-1"
          >
            <XCircle className="w-4 h-4" />
            {cancelText}
          </button>
        )}
      </div>

      <div className="text-sm text-gray-600">
        {t('vehicles.wizard.stepOf', { current: currentStep, total: totalSteps })}
      </div>

      {currentStep < totalSteps ? (
        <button
          onClick={onNext}
          className={`px-6 py-2 ${accentClass} text-white rounded-lg flex items-center gap-2`}
        >
          {t('common.actions.next')}
          <ChevronRight className="w-5 h-5" />
        </button>
      ) : (
        <button
          onClick={onSubmit}
          disabled={!canSubmit}
          className={`px-6 py-2 rounded-lg flex items-center gap-2 ${
            !canSubmit
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-green-600 text-white hover:bg-green-700'
          }`}
        >
          <CheckCircle className="w-5 h-5" />
          {submitText}
        </button>
      )}
    </div>
  );
}

export default WorkflowFooter;
